// 🎯 Componente para crear nueva recepción de devoluciones con confirmación
import { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { useToast } from '@shared/hooks/use-toast';
import { ReturnedApiAdapter } from '../../adapters/ReturnedApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';

// 🎯 Tipo para la referencia del modal de confirmación
type ConfirmModalRef = {
  open: () => void;
};

export interface CreateButtonProps {
  onSuccess?: () => void;
  onRefetch?: () => void;
  className?: string;
}

export const CreateButton = ({ onSuccess, onRefetch, className = '' }: CreateButtonProps) => {
  const { t } = useTranslation('returnedModule');
  const { toast } = useToast();
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  // 🎯 Función para confirmar la creación
  const handleConfirmCreate = useCallback(async () => {
    console.log('🔍 CreateButton - Confirmando creación de recepción');

    try {
      const returnedAdapter = new ReturnedApiAdapter(apiClientWH);
      const response = await returnedAdapter.createReturnedReception();

      console.log('🔍 CreateButton - API response:', response);

      if (response.success) {
        toast({
          title: t('toasts.success.title'),
          description: response.message || t('toasts.create.success'),
          variant: 'default',
        });

        // 🎯 Actualizar datos
        onSuccess?.();
        onRefetch?.();
      } else {
        toast({
          title: t('toasts.error.title'),
          description: response.message || t('toasts.create.error'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.log('🔍 CreateButton - API error:', error);
      toast({
        title: t('toasts.error.title'),
        description: t('toasts.create.unexpected'),
        variant: 'destructive',
      });
    }
  }, [t, toast, onSuccess, onRefetch]);

  // 🎯 Función para manejar la creación de nueva recepción
  const handleCreateReturned = useCallback(() => {
    console.log('🔍 CreateButton - Abriendo modal de confirmación');
    confirmModalRef.current?.open(
      handleConfirmCreate,
      undefined, // onCancel
      t('modal.create.title'),
      t('modal.create.message'),
      t('modal.create.confirm'),
      t('modal.create.cancel'),
    );
  }, [t, handleConfirmCreate]);

  return (
    <>
      <Button
        variant="primary"
        size="lg"
        onClick={handleCreateReturned}
        className={`shrink-0 ${className}`}
        data-testid="create-returned-button"
      >
        {t('buttons.create')}
      </Button>

      <ConfirmModal ref={confirmModalRef} />
    </>
  );
};
